import * as React from "react";
import type { Category } from "../../../../server/results";
import type { User } from "../../models/users";
import { Button } from "../../DesignSystem/Button";
import { EntryList } from "../../DesignSystem/EntryList";

export type ResultColumnProps = {
  categoryName: string;
  entries: Category;
  limit?: number;
  linkToFullResults?: string;
  entryUsers: { [entry_id: number]: User[] };
  fullWidth: boolean;
};

const DEFAULT_LIMIT = Number.POSITIVE_INFINITY;

export const ResultColumn = ({
  categoryName,
  linkToFullResults,
  entries,
  entryUsers,
  limit = DEFAULT_LIMIT,
}: ResultColumnProps): JSX.Element => {
  const slicedEntries = entries.slice(0, limit);

  // Convert entryUsers map to avatar_urls and usernames arrays expected by EntryList
  const avatar_urls = slicedEntries.map((result) => {
    const users = entryUsers[result.entry.id] || [];
    return users.map((u) => u.avatar_url).filter((url) => !!url);
  });

  const usernames = slicedEntries.map((result) => {
    const users = entryUsers[result.entry.id] || [];
    return users.map((u) => u.username);
  });

  // Extract scores for the category
  const scores = slicedEntries.map((result) => result.score);

  // Extract entry JSONs
  const entryJSONs = slicedEntries.map((result) => result.entry);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white/90 p-4 dark:border-white/10 dark:bg-slate-800">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {categoryName}
        </h3>
        {linkToFullResults && (
          <div>
            <Button to={linkToFullResults} variant="text">
              See all &rarr;
            </Button>
          </div>
        )}
      </div>

      <div className="-mx-2">
        <EntryList
          hideFilter
          hidePlayAll
          entries={entryJSONs}
          hideStats
          avatar_urls={avatar_urls}
          scores={scores}
          usernames={usernames}
        />
      </div>
    </div>
  );
};
